import { BotGalleryFilters } from 'components/filters/BotGalleryFilters';
import Link from 'next/link';
import { useMutation } from 'react-query';
import { searchBots, searchCards, recordSearchClick } from 'api/bot';
import { useEffect, useState } from 'react';
import { Bot, BotCard, SearchParams, FetchBotsParams, SearchResponse, SearchBotCardRead } from 'api/bot/types';
import { generateArray } from 'utils';
import { BotSkeleton } from 'components/skeletons/BotSkeleton';
import { BotCardItem } from 'components/lists/cards/BotCardItem';
import { useRouter } from 'next/router';
import { ClipLoader } from 'react-spinners';

type SearchType = 'bots' | 'cards' | null;

type QueryIds = {
  bots?: string;
  cards?: string;
};

// Move outside component to prevent recreation
const interleaveResults = (bots: Bot[], cards: BotCard[]) => {
  const total = bots.length + cards.length;
  if (total === 0) return [];

  // Create a stable random seed based on the content
  const seed = bots.map(b => b.id).join('') + cards.map(c => c.id).join('');
  const pseudoRandom = (index: number) => {
    const hash = seed.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, index);
    return (hash % 100) / 100;
  };

  const result: (Bot | BotCard)[] = [];
  let botIndex = 0;
  let cardIndex = 0;

  // Process pairs of items
  let pairIndex = 0;
  while (botIndex < bots.length && cardIndex < cards.length) {
    if (pseudoRandom(pairIndex++) < 0.5) {
      result.push(bots[botIndex++], cards[cardIndex++]);
    } else {
      result.push(cards[cardIndex++], bots[botIndex++]);
    }
  }

  // Add remaining items from either array
  result.push(...bots.slice(botIndex));
  result.push(...cards.slice(cardIndex));

  return result;
};

export const getServerSideProps = async ({
  params,
  query
}: {
  params: { query: string },
  query: {
    type?: string,
    source?: string
  }
}) => {
  const searchType = query.type === 'cards' || query.type === 'bots' ? query.type : null;
  const searchSource = query.source;

  return {
    props: {
      searchQuery: params.query,
      searchType,
      searchSource
    }
  };
};

const ExplorePage = ({ searchQuery, searchType, searchSource }: { searchQuery: string, searchType: SearchType, searchSource: string }) => {
  const router = useRouter();

  // Initial state setup
  const [items, setItems] = useState<(Bot | BotCard)[]>([]);
  const [maxPage, setMaxPage] = useState<number | null>(null);
  const [params, setParams] = useState<SearchParams>({
    page: 0,
    q: searchQuery
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Search mutations
  const botsSearch = useMutation(['search', 'bots'], searchBots, {
    retry: false,
  });

  const cardsSearch = useMutation(['search', 'cards'], searchCards, {
    retry: false,
  });

  // Add state for query IDs
  const [queryIds, setQueryIds] = useState<QueryIds>({});

  // Handle initial load
  useEffect(() => {
    setItems([]);
    setParams({
      page: 0,
      q: searchQuery,
      source: searchSource
    });
    performSearch({
      page: 0,
      q: searchQuery,
      source: searchSource
    });
  }, [searchQuery, searchType, searchSource]);

  // Handle param changes (pills/filters)
  const handleParamsChange = (value: string, key: keyof SearchParams | keyof FetchBotsParams) => {
    if (!value) return;

    if (key === 'q') {
      router.push({
        pathname: `/explore/${encodeURIComponent(value.toLowerCase())}`,
        query: { type: searchType }
      });
      return;
    }

    setParams(prev => ({ ...prev, [key]: value, page: 0 }));
  };

  // Main search function
  const performSearch = async (searchParams: SearchParams, appendResults = false) => {
    try {
      if (appendResults) {
        setIsLoadingMore(true);
      }

      if (searchType === null) {
        console.log('Starting combined search...', {
          time: new Date().toISOString(),
          searchParams,
          appendResults
        });

        const [botsResponse, cardsResponse] = await Promise.all([
          botsSearch.mutateAsync(searchParams),
          cardsSearch.mutateAsync(searchParams)
        ]);

        // Store query IDs only on initial search
        if (!appendResults) {
          setQueryIds({
            bots: botsResponse.search_id,
            cards: cardsResponse.search_id
          });
        }

        const combinedResults = interleaveResults(
          botsResponse.data as Bot[],
          cardsResponse.data as BotCard[]
        );

        setItems(prev => appendResults ? [...prev, ...combinedResults] : combinedResults);

        if (!appendResults) {
          setMaxPage(Math.max(
            botsResponse.total ?? botsResponse.count ?? 0,
            cardsResponse.total ?? cardsResponse.count ?? 0
          ));
        }
      } else {
        try {
          const mutation = searchType === 'cards' ? cardsSearch : botsSearch;
          const response = await mutation.mutateAsync(searchParams);

          // Store query ID only on initial search
          if (!appendResults) {
            setQueryIds({
              [searchType]: response.search_id
            });
          }

          setItems(
            prev => appendResults ?
              [...prev, ...(response.data as Bot[] | BotCard[])] :
              (response.data as Bot[] | BotCard[]));
          if (!appendResults) {
            setMaxPage(response.total ?? response.count ?? null);
          }
        } catch (error) {
          console.error('Search error:', error);
        }
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Update isLoading check
  const isLoading = cardsSearch.isLoading || botsSearch.isLoading;

  const isMaxExceeded = typeof maxPage === 'number' && items.length >= maxPage;

  // Add click handler function
  const handleItemClick = async (item: Bot | BotCard) => {
    const itemType = 'key' in item ? 'cards' : 'bots';
    const queryId = queryIds[itemType];

    if (queryId) {
      try {
        await recordSearchClick(queryId, item.id);
      } catch (error) {
        console.error('Failed to record click:', error);
      }
    }
  };

  return (
    <>
      <div className="container">
        <div className="flex flex-col gap-8 md:gap-32">
          <div className="flex flex-col pb-10">
            <BotGalleryFilters
              onParamsChange={handleParamsChange}
              query={searchQuery}
              source={searchSource}
            />

            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {items.length
                ? items.map((item) => (
                    <BotCardItem
                      key={`${'key' in item ? 'card' : 'bot'}-${item.id}`}
                      data={item as Bot | BotCard}
                      onClick={() => handleItemClick(item)}
                    />
                  ))
                : generateArray(8).map((i) => <BotSkeleton key={i} />)}
            </div>

            {items.length > 0 && (
              <div className="flex flex-col items-center gap-2 mt-6">
                <button
                  className="btn btn-primary hover:text-brand hover:border-brand"
                  disabled={isLoading || isLoadingMore || isMaxExceeded}
                  onClick={() => {
                    setParams((prev) => {
                      const newParams = { ...prev, page: (prev.page || 0) + 1 };
                      performSearch(newParams, true);
                      return newParams;
                    });
                  }}
                >
                  {isMaxExceeded ? 'No more results' : (
                    isLoadingMore ? (
                      <div className="flex items-center gap-2">
                        <ClipLoader size={16} color="currentColor" />
                        Loading...
                      </div>
                    ) : 'Load more'
                  )}
                </button>

                <span>
                  or{' '}
                  <Link href="/create" className="hover:text-brand gap-2" prefetch={false}>
                    <u>Create your own!</u>
                  </Link>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ExplorePage;
